import type { ApiResponse } from '../../api/contract';
import type { TransactionDetail } from './types';
import { irreversibleKeyHint, toSafeFailure, toSafeTrace } from './types';

type ApiDetail = ApiResponse<'/api/v1/transactions/{id}', 'get'>['data'];

const partner = Object.freeze({
  id: '00000000-0000-4000-8000-000000000101',
  code: 'PTN-A',
  name: 'Alpha Partner',
});

const calculated: ApiDetail = {
  id: '00000000-0000-4000-8000-000000000401',
  partner,
  partnerTransactionId: 'ptx-a',
  persistedStatus: 'CALCULATED',
  reversalState: 'REVERSED',
  submittedUsername: 'member.alpha',
  submittedPartnerUserId: 'player-1',
  submittedGameTypeCode: 'SLOT',
  submittedGameCode: 'SLOT-ROYAL',
  gameRound: 'round-a',
  actualPlayAmount: '100.000000',
  outcome: 'WIN',
  winLossAmount: '25.500000',
  currencyCode: 'THB',
  currencyMinorUnit: 2,
  playAt: '2026-07-21T23:00:00-04:00',
  receivedAt: '2026-07-22T10:00:00+07:00',
  resolution: {
    memberId: '00000000-0000-4000-8000-000000000501',
    agentId: '00000000-0000-4000-8000-000000000401',
    agentCode: 'AGT-A',
    uplineMemberId: '00000000-0000-4000-8000-000000000502',
    uplineUsername: 'member.beta',
    downlineUsername: 'member.alpha',
    gameId: '00000000-0000-4000-8000-000000000911',
    gameCode: 'SLOT-ROYAL',
  },
  failure: null,
  requestMetadata: { trace: 'trace-transaction-a', authorization: '[REDACTED]' },
  calculation: {
    id: '00000000-0000-4000-8000-000000000501',
    assignmentId: '00000000-0000-4000-8000-000000000601',
    commissionRuleId: '00000000-0000-4000-8000-000000000701',
    agentRatePercent: '2.500000',
    affiliateRatePercent: '5.000000',
    agentCommissionUnrounded: '2.500000',
    affiliateCommissionUnrounded: '5.000000',
    agentCommission: '2.500000',
    affiliateCommission: '5.000000',
    totalCommission: '7.500000',
    roundingMode: 'ROUND_HALF_UP',
    calculationVersion: 3,
    calculatedAt: '2026-07-21T23:00:01-04:00',
    calculatedBy: '00000000-0000-4000-8000-000000000001',
  },
  commissionLedgerEntries: [
    {
      id: '00000000-0000-4000-8000-000000000801',
      partner,
      agentId: '00000000-0000-4000-8000-000000000401',
      agentCode: 'AGT-A',
      beneficiaryType: 'AGENT',
      beneficiaryId: '00000000-0000-4000-8000-000000000401',
      beneficiaryUsername: null,
      entryType: 'EARN',
      signedAmount: '2.500000',
      currencyCode: 'THB',
      currencyMinorUnit: 2,
      source: {
        transactionId: '00000000-0000-4000-8000-000000000401',
        partnerTransactionId: 'ptx-a',
        calculationId: '00000000-0000-4000-8000-000000000501',
        reversalId: null,
        sourceEntryId: null,
        approvalRequestId: null,
        adjustmentId: null,
      },
      walletPosting: {
        entryId: '00000000-0000-4000-8000-000000001101',
        walletId: '00000000-0000-4000-8000-000000001001',
        entryType: 'COMMISSION_EARN',
        availableDelta: '2.500000',
        availableAfter: '102.500000',
        expiresAt: null,
        effectiveAt: '2026-07-21T23:00:02-04:00',
      },
      reasonCode: null,
      reason: null,
      effectiveAt: '2026-07-21T23:00:02-04:00',
      createdAt: '2026-07-21T23:00:02-04:00',
      createdBy: '00000000-0000-4000-8000-000000000001',
    },
    {
      id: '00000000-0000-4000-8000-000000000802',
      partner,
      agentId: '00000000-0000-4000-8000-000000000401',
      agentCode: 'AGT-A',
      beneficiaryType: 'AFFILIATE_MEMBER',
      beneficiaryId: '00000000-0000-4000-8000-000000000501',
      beneficiaryUsername: 'member.alpha',
      entryType: 'EARN',
      signedAmount: '5.000000',
      currencyCode: 'THB',
      currencyMinorUnit: 2,
      source: {
        transactionId: '00000000-0000-4000-8000-000000000401',
        partnerTransactionId: 'ptx-a',
        calculationId: '00000000-0000-4000-8000-000000000501',
        reversalId: null,
        sourceEntryId: null,
        approvalRequestId: null,
        adjustmentId: null,
      },
      walletPosting: {
        entryId: '00000000-0000-4000-8000-000000001102',
        walletId: '00000000-0000-4000-8000-000000001002',
        entryType: 'COMMISSION_EARN',
        availableDelta: '5.000000',
        availableAfter: '205.000000',
        expiresAt: null,
        effectiveAt: '2026-07-21T23:00:02-04:00',
      },
      reasonCode: null,
      reason: null,
      effectiveAt: '2026-07-21T23:00:02-04:00',
      createdAt: '2026-07-21T23:00:02-04:00',
      createdBy: '00000000-0000-4000-8000-000000000001',
    },
  ],
  idempotency: {
    id: '00000000-0000-4000-8000-000000000751',
    key: '00000000-0000-4000-8000-000000000777',
    state: 'COMPLETED',
    responseStatus: 200,
    createdAt: '2026-07-21T23:00:00-04:00',
    completedAt: '2026-07-21T23:00:03-04:00',
    expiresAt: '2026-07-22T23:00:00-04:00',
  },
  reversal: {
    id: '00000000-0000-4000-8000-000000000601',
    partnerReversalId: 'partner-reversal-a',
    reasonCode: 'PARTNER_REQUEST',
    reason: 'Partner reversed the original transaction',
    approvalRequestId: null,
    createdAt: '2026-07-22T00:00:00-04:00',
    createdBy: '00000000-0000-4000-8000-000000000001',
  },
};

const failed: ApiDetail = {
  ...calculated,
  id: '00000000-0000-4000-8000-000000000402',
  partnerTransactionId: 'ptx-b',
  persistedStatus: 'FAILED',
  reversalState: 'NOT_REVERSED',
  submittedUsername: 'member.beta',
  submittedPartnerUserId: null,
  submittedGameCode: 'LIVE-BACCARAT',
  actualPlayAmount: '50.250000',
  outcome: 'LOSS',
  winLossAmount: '-50.250000',
  currencyCode: 'USD',
  resolution: null,
  failure: {
    code: 'NORMALIZATION_FAILED',
    details: { safeCode: 'INVALID_GAME', token: '[REDACTED]' },
  },
  requestMetadata: { trace: 'trace-transaction-b' },
  calculation: null,
  commissionLedgerEntries: [],
  idempotency: {
    ...calculated.idempotency,
    id: '00000000-0000-4000-8000-000000000752',
    key: '00000000-0000-4000-8000-000000000778',
  },
  reversal: null,
};

export const transactionApiFixtures = Object.freeze<readonly ApiDetail[]>([calculated, failed]);

export const transactionFixtures = Object.freeze<readonly TransactionDetail[]>(
  transactionApiFixtures.map((source, index) => {
    const { failure, idempotency, requestMetadata, ...detail } = source;
    const { key, ...safeIdempotency } = idempotency;
    return {
      ...detail,
      failure: toSafeFailure(failure),
      idempotency: { ...safeIdempotency, keyHint: irreversibleKeyHint(key) },
      requestId: index === 0 ? 'req-transaction-a' : 'req-transaction-b',
      requestTrace: toSafeTrace(requestMetadata),
    } as TransactionDetail;
  }),
);
